import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Close,
  DotsVertical,
  Pen,
  QuestionCircle,
} from 'flowbite-react-icons/outline'
import { Button } from '@helix-ds/react'
import { useEffect, useRef, useState } from 'react'
import { CopyableValue } from '@/components/common/CopyableValue'
import { TruncatedText } from '@/components/common/Tooltip'
import { formatMrnForDisplay, rawMrnValue } from '@/utils/orderFormUtils'
import { useNavigate } from 'react-router-dom'
import type { OrderStep, PatientInfo } from './OrderTypes'
import { OrderStepsRow } from './OrderStepsRow'

export type HeaderMode = 'start' | 'samples' | 'readonly' | 'submit'

interface EditOption {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
}

interface CommonProps {
  patient: PatientInfo
  steps: OrderStep[]
  /** Accepted for call-site compatibility; not used for rendering (steps carry their own status). */
  currentStep?: number
  onStepClick?: (stepId: string) => void
  onHelpClick?: () => void
  isLoading?: boolean
  isNavigationDisabled?: boolean
  showHelp?: boolean
  /** When set, shows a control to edit proband demographics (e.g. standalone / non-Epic). */
  onEditPatient?: () => void
  /** Hard-delete the in-progress draft (GEN-3272). Shown in the order actions menu. */
  onDiscardOrder?: () => void
  /**
   * Extra classes for the sticky header container. Standalone pages pass a
   * negative horizontal margin (`-mx-6`) so the header breaks out of the page's
   * content padding and sits edge-to-edge with the full-width nav bar.
   */
  containerClassName?: string
  /**
   * Optional leading control rendered inline at the start of the title row, to
   * the left of the patient name. Used by the R5 side-nav pages to place the
   * narrow-screen "Open order navigation" burger ({@link OrderNavToggle}) next
   * to the patient name instead of orphaned above the content row. The control
   * is responsible for its own `lg:hidden` visibility.
   */
  navToggle?: React.ReactNode
}

export interface SubmitModeProps extends CommonProps {
  mode: 'submit'
  onBack: () => void
  onSubmit: () => void
  submitDisabled: boolean
  isSubmitting: boolean
  /** Explicit Back disabled state; defaults to `isSubmitting` when omitted. */
  backDisabled?: boolean
  /** CTA label when not submitting; defaults to `'Submit'`. */
  submitLabel?: string
}

export interface OtherModeProps extends CommonProps {
  mode: 'start' | 'samples' | 'readonly'
  onSubmit?: () => void
  canSubmit?: boolean
  editOptions?: EditOption[]
  onUpdateOrder?: () => void
  hasChanges?: boolean
  onStartNewOrder?: () => void
}

export type OrderHeaderProps = SubmitModeProps | OtherModeProps

export function OrderHeader(props: OrderHeaderProps) {
  const {
    patient,
    steps,
    onStepClick,
    onHelpClick,
    isLoading = false,
    isNavigationDisabled = false,
    showHelp = true,
    onEditPatient,
    onDiscardOrder,
    containerClassName,
    navToggle,
  } = props

  const navigate = useNavigate()
  const [editDropdownOpen, setEditDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false)
  const optionsMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setEditDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Header options (kebab) menu: outside-click + Escape to dismiss, mirroring
  // OrderInProgress's "Order actions" kebab.
  useEffect(() => {
    if (!optionsMenuOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setOptionsMenuOpen(false)
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOptionsMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [optionsMenuOpen])

  const handleStartNewOrder = () => {
    if (props.mode !== 'submit') {
      if (props.onStartNewOrder) {
        props.onStartNewOrder()
      } else {
        navigate('/order/start')
      }
    }
  }

  // Render-level patient fallbacks — never write placeholder strings into patient fields.
  const displayName = patient.name.trim() || 'Patient'
  const mrnDisplay = rawMrnValue(patient.mrn) ? formatMrnForDisplay(patient.mrn) : 'MRN not available'
  const mrnRaw = rawMrnValue(patient.mrn)
  const displayAge = patient.age.trim() || 'Age unavailable'
  // '—' is not a date — it signals "no DOB recorded" so the slot is never silently blank.
  const displayDob = patient.dob.trim() || '—'
  // Pre-composed so the whole line (not just the leading field) is what gets
  // ellipsised and what the tooltip reveals when Epic renders us in a sidebar.
  const ageLine = `${displayAge}${patient.sex ? ` • ${patient.sex}` : ''}`
  const dobLine = `${displayDob}${patient.gestationalAge ? ` • ${patient.gestationalAge}` : ''}`

  return (
    <>
      <div
        data-sticky-page-header
        className={`sticky top-0 z-40 flex flex-col gap-4 border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm sm:p-6 ${containerClassName ?? ''}`}
      >
        <div className="flex flex-row flex-wrap items-center gap-3 md:flex-nowrap md:gap-6">
          <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-6 md:flex-none">
            {navToggle}
            <div className="flex min-w-0 items-start gap-2 sm:items-center sm:gap-3">
              <div className="flex min-w-0 flex-col gap-1">
                <TruncatedText
                  as="h1"
                  text={displayName}
                  className="text-xl font-semibold leading-7 tracking-tight text-text-heading dark:text-white sm:text-2xl sm:leading-8"
                />
                {mrnRaw ? (
                  <CopyableValue
                    copyValue={mrnRaw}
                    ariaLabel="Copy MRN"
                    truncate
                    overflowLabel={mrnDisplay}
                    className="w-fit max-w-full text-xs font-medium leading-5 text-text-body dark:text-gray-400"
                  >
                    {mrnDisplay}
                  </CopyableValue>
                ) : (
                  <span className="text-xs font-medium leading-5 text-text-body dark:text-gray-400">{mrnDisplay}</span>
                )}
              </div>
            </div>

            <div className="h-10 w-px shrink-0 bg-border-base sm:h-14" />

            {/* `min-w-0` (rather than `shrink-0`) so this block can compress and
                ellipsise in Epic's narrow sidebar instead of forcing the patient
                name to absorb all of the overflow. */}
            <div className="flex min-w-0 flex-col gap-1">
              <TruncatedText text={ageLine} className="text-sm font-bold leading-5 text-text-heading dark:text-white" />
              <TruncatedText text={dobLine} className="text-xs font-medium leading-5 text-text-body dark:text-gray-400" />
            </div>
          </div>

          {/* Inline progress stepper: shares the header row with the age info on
              md+ screens and wraps to its own full-width line below on small
              screens (order-last) so it doesn't push the actions off-row. */}
          {steps.length > 0 && (
            <>
              <div className="hidden h-14 w-px bg-border-base md:block" />
              <div className="order-last w-full min-w-0 md:order-none md:w-auto md:flex-1 md:px-2">
                <OrderStepsRow
                  variant="wizard"
                  steps={steps}
                  onStepClick={onStepClick}
                  isNavigationDisabled={isNavigationDisabled}
                  isLoading={isLoading}
                />
              </div>
              <div className="hidden h-14 w-px bg-border-base md:block" />
            </>
          )}

          <div className="flex shrink-0 items-center justify-end gap-2">
          {(onEditPatient || onDiscardOrder) && (
            <div className="relative shrink-0" ref={optionsMenuRef}>
              <button
                type="button"
                id="order-header-options-trigger"
                aria-label="Order actions"
                aria-haspopup="menu"
                aria-expanded={optionsMenuOpen}
                aria-controls="order-header-options-menu"
                onClick={() => setOptionsMenuOpen((open) => !open)}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border-base-medium bg-background-secondary-medium px-4 py-2.5 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-background-tertiary dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <DotsVertical className="h-5 w-5 shrink-0" aria-hidden />
                <span>More</span>
              </button>
              {optionsMenuOpen && (
                <div
                  id="order-header-options-menu"
                  role="menu"
                  aria-labelledby="order-header-options-trigger"
                  className="absolute right-0 top-full z-50 mt-2 min-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 py-1 shadow-lg sm:min-w-56"
                >
                  {onEditPatient && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOptionsMenuOpen(false)
                        onEditPatient()
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                    >
                      <Pen className="h-4 w-4 shrink-0" aria-hidden />
                      Edit Demographics
                    </button>
                  )}
                  {onDiscardOrder && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOptionsMenuOpen(false)
                        onDiscardOrder()
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-error-600 transition-colors hover:bg-error-50 dark:hover:bg-gray-800"
                    >
                      <Close className="h-4 w-4 shrink-0" aria-hidden />
                      Discard Order
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {showHelp && (
            <button onClick={onHelpClick} className="btn-icon-lg hidden sm:flex" aria-label="Help">
              <QuestionCircle className="h-5 w-5" />
            </button>
          )}

          {props.mode === 'submit' && (
            <>
              <button
                type="button"
                aria-label="Back"
                onClick={props.onBack}
                disabled={props.backDisabled ?? props.isSubmitting}
                className="inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border border-border-base-medium dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 px-3 py-2 text-xs font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={() => {
                  if (props.isSubmitting) return
                  props.onSubmit()
                }}
                disabled={props.submitDisabled}
                aria-busy={props.isSubmitting}
                className="shrink-0 whitespace-nowrap focus-visible:ring-brand-600"
              >
                <span>{props.isSubmitting ? 'Submitting\u2026' : (props.submitLabel?.trim() || 'Submit')}</span>
              </Button>
            </>
          )}

          {props.mode === 'start' && (
            <>
              <Button
                variant="primary"
                size="md"
                onClick={props.onSubmit}
                disabled={!props.canSubmit || isLoading}
                className="shrink-0 whitespace-nowrap focus-visible:ring-brand-600"
              >
                <span className="hidden sm:inline">Continue</span>
                <span className="sm:hidden">Continue</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {props.mode === 'samples' && (
            <>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setEditDropdownOpen(!editDropdownOpen)}
                  className="inline-flex items-center justify-center gap-1 rounded-full border border-border-base-medium dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 px-3 py-2 text-xs font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700 sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm"
                >
                  <Pen className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit Order</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${editDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {editDropdownOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 py-1 shadow-lg">
                    {(props.editOptions ?? []).map((option) => {
                      const IconComponent = option.icon
                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            option.onClick()
                            setEditDropdownOpen(false)
                          }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                        >
                          <IconComponent className="h-4 w-4 text-text-body dark:text-gray-400" />
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={props.onUpdateOrder}
                disabled={!props.hasChanges}
                className="focus-visible:ring-brand-600"
              >
                <span className="hidden sm:inline">Update Order</span>
                <span className="sm:hidden">Update</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {props.mode === 'readonly' && (
            <Button variant="primary" size="md" onClick={handleStartNewOrder} className="focus-visible:ring-brand-600">
              <span className="hidden sm:inline">Start New Order</span>
              <span className="sm:hidden">New Order</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          </div>
        </div>
      </div>
    </>
  )
}
